import React, { useEffect, useMemo, useRef } from "react";
import { Button } from "reactstrap";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { ContainerItem } from "../helpers/ipInventory.types";
import { getContainerColumns } from "../helpers/ipInventory.constant";
import { IP_TOTAL_CONTAINERS_OPTIONS, CONTAINER_CONDITION_OPTIONS } from "api/modules/common.option";

interface ContainerManagementSectionProps {
  containersList: ContainerItem[];
  setContainersList: React.Dispatch<React.SetStateAction<ContainerItem[]>>;
  containerError: string | null;
  setContainerError: React.Dispatch<React.SetStateAction<string | null>>;
  selectedStudyCode?: string;
  setValue: any;
  onRegisterValidate?: (validateFn: () => boolean) => void;
  isDisabled?: boolean;
}

const ContainerManagementSection: React.FC<ContainerManagementSectionProps> = ({
  containersList,
  setContainersList,
  containerError,
  setContainerError,
  selectedStudyCode,
  setValue,
  onRegisterValidate,
  isDisabled = false,
}) => {
  const initialMinNoRef = useRef<number | null>(null);

  // Track the minimum starting container number when containers are loaded
  useEffect(() => {
    if (!containersList || containersList.length === 0) {
      initialMinNoRef.current = null;
      return;
    }

    if (initialMinNoRef.current === null && containersList.length > 0) {
      const minNo = containersList.reduce((min, c) => {
        const num = parseInt(String(c.containernumber || ""), 10);
        return !isNaN(num) && num > 0 && num < min ? num : min;
      }, Infinity);

      if (minNo !== Infinity) {
        initialMinNoRef.current = minNo;
      }
    }
  }, [containersList]);

  // Update totalcontainers, receivedqty, and availablequantity form fields whenever containersList changes
  useEffect(() => {
    const hasExistingFlag = containersList.some((c) => c.isExisting);
    const targetContainers = hasExistingFlag
      ? containersList.filter((c) => !c.isExisting)
      : containersList;

    const newContainersCount = targetContainers.length;
    const sumReceived = targetContainers.reduce((acc, curr) => acc + (Number(curr.initialquantity) || 0), 0);
    const sumAvailable = targetContainers
      .filter((c) => c.isusable !== false)
      .reduce((acc, curr) => acc + (Number(curr.initialquantity) || 0), 0);

    setValue("totalcontainers", newContainersCount > 0 ? newContainersCount.toString() : null);
    setValue("receivedqty", newContainersCount > 0 ? sumReceived.toString() : null);
    setValue("actualreceivedqty", newContainersCount > 0 ? sumAvailable.toString() : null);
  }, [containersList, setValue]);

  // Handle container table field changes
  const handleContainerChange = (index: number, field: keyof ContainerItem, val: any) => {
    setContainerError(null);
    setContainersList((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: val };
      return next;
    });
  };

  // Helper to validate container list items (only validate new containers)
  const validateContainers = (): boolean => {
    const newContainers = containersList.filter((c) => !c.isExisting);
    if (!newContainers || newContainers.length === 0) {
      const msg = "Please add at least one new container.";
      setContainerError(msg);
      return false;
    }

    for (let i = 0; i < containersList.length; i++) {
      const item = containersList[i];
      if (item.isExisting) continue;

      const missing: string[] = [];

      if (!String(item.initialquantity ?? "").trim() || Number(item.initialquantity) <= 0) {
        missing.push("Container Quantity");
      }
      if (!item.containertype) {
        missing.push("Container Type");
      }
      if (item.containercondition === null || item.containercondition === undefined) {
        missing.push("Container Condition");
      }

      if (missing.length > 0) {
        const containerNo = item.containernumber || String(i + 1).padStart(2, "0");
        const missingText = missing.join(", ").replace(/, ([^,]*)$/, " & $1");
        const msg = `Please enter/select ${missingText} for Container ${containerNo}.`;
        setContainerError(msg);
        return false;
      }
    }

    setContainerError(null);
    return true;
  };

  // Register validation callback with parent component if requested
  useEffect(() => {
    if (onRegisterValidate) {
      onRegisterValidate(validateContainers);
    }
  }, [containersList, onRegisterValidate]);

  // Helper to resequence container numbers starting from the initial container's number
  const resequenceContainers = (containers: ContainerItem[]): ContainerItem[] => {
    if (!containers || containers.length === 0) return containers;

    // A. Check if any container has isExisting: true (Add Page context with pre-filled master records)
    const hasExistingFlag = containers.some((c) => c.isExisting);

    if (hasExistingFlag) {
      const existingContainers = containers.filter((c) => c.isExisting);
      const maxExistingNo = existingContainers.reduce((max, c) => {
        const num = parseInt(String(c.containernumber || ""), 10);
        return !isNaN(num) && num > max ? num : max;
      }, 0);

      let newCounter = 0;
      return containers.map((item) => {
        if (item.isExisting) {
          return item;
        }
        newCounter += 1;
        return {
          ...item,
          containernumber: String(maxExistingNo + newCounter).padStart(2, "0"),
        };
      });
    }

    // B. Edit Page or standard list: preserve initial minimum starting container number (e.g. 05 -> 05, 06, 07...)
    const baseMin = initialMinNoRef.current;
    const firstNum = parseInt(String(containers[0]?.containernumber || "1"), 10);
    const startNum = baseMin !== null && !isNaN(baseMin) ? baseMin : (!isNaN(firstNum) && firstNum > 0 ? firstNum : 1);

    return containers.map((item, idx) => ({
      ...item,
      containernumber: String(startNum + idx).padStart(2, "0"),
    }));
  };

  // Add a new empty container item
  const handleAddContainer = () => {
    if (!validateContainers()) return;

    setContainersList((prev) => {
      const added: ContainerItem[] = [
        ...prev,
        {
          containernumber: "",
          initialquantity: "",
          containertype: null,
          containercondition: null,
          containerremarks: null,
          isusable: true,
          isExisting: false,
        },
      ];
      return resequenceContainers(added);
    });
  };

  // Delete a specific container row by index
  const handleDeleteContainer = (index: number) => {
    setContainerError(null);
    setContainersList((prev) => {
      const filtered = prev.filter((_, idx) => idx !== index);
      return resequenceContainers(filtered);
    });
  };

  const effectiveDisabled = isDisabled || !selectedStudyCode;

  // CommonTableIndexWise Column Configuration for Containers
  const containerColumns = useMemo(
    () =>
      getContainerColumns(
        containersList,
        handleContainerChange,
        handleDeleteContainer,
        effectiveDisabled,
        IP_TOTAL_CONTAINERS_OPTIONS,
        CONTAINER_CONDITION_OPTIONS
      ),
    [containersList, effectiveDisabled]
  );

  if (!selectedStudyCode) return null;

  return (
    <div className="mt-4 p-3 border rounded-3 bg-light shadow-sm">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="fw-bold mb-1 text-dark d-flex align-items-center" style={{ fontSize: "13px" }}>
            <i className="ri-list-check-2 me-2 text-primary" style={{ fontSize: "15px" }}></i>
            Container Management <span className="text-danger ms-1">*</span>
          </h6>
          <small className="text-muted" style={{ fontSize: "11px" }}>
            Manage container details, quantities, status, and remarks. (Required)
          </small>
        </div>
        <div>
          <Button
            type="button"
            color="primary"
            size="sm"
            outline
            className="d-flex align-items-center gap-1 shadow-sm px-2.5 py-1"
            onClick={handleAddContainer}
            disabled={effectiveDisabled}
            style={{ fontSize: "11px" }}
          >
            <i className="ri-add-circle-line"></i> Add Container
          </Button>
        </div>
      </div>

      {containerError && (
        <div className="text-danger mb-2 d-flex align-items-center gap-1" style={{ fontSize: "11px", fontWeight: 500 }}>
          <i className="ri-error-warning-line me-1"></i>
          <span>{containerError}</span>
        </div>
      )}

      <CommonTableIndexWise
        columns={containerColumns}
        data={containersList}
        hideAction={true}
        paginationShow={false}
        emptyText="No containers added. Click 'Add Container' to start."
      />
    </div>
  );
};

export default ContainerManagementSection;
